import { type Evaluated, formatMeasurement, angleMarkNominal } from '@/domain/construction';
import { formulaLabel } from '@/domain/expression';
import { type Bounds, conics } from '@/domain/geometry';
import { angle as angleMath, type Vec2, vec } from '@/domain/math';
import { LineStyle, PointStyle, type Style } from '@/domain/model';
import { type SolidCurve } from '@/domain/solid';

import { createMeasurer, layoutText, type TextMeasurer } from '../text';

import { angleMarkGeometry, rightAngleCorners, tickRadii, valuePosition } from './angleMark';
import { axisStroke, nameInk, numberInk, tickStroke } from './axesStyle';
import { clipLineToBounds, clipRayToBounds } from './clip';
import { dashedIfCovered } from './hiddenLines';

/**
 * Painters for evaluated geometry.
 *
 * Everything is painted in **screen space**, with world coordinates converted per point,
 * rather than by putting the canvas under the camera transform. Three consequences, all
 * of them what a geometry editor wants:
 *
 * - Stroke width and point size are in screen pixels, so a 1.5px line stays 1.5px at
 *   every zoom instead of thickening as the user zooms in.
 * - Dash patterns keep their on-screen rhythm.
 * - Unbounded geometry is clipped in world space against the visible rectangle, which is
 *   both exact and stable at extreme zoom.
 *
 * The trade-off is one transform per vertex. For the entity counts this editor targets
 * that is far cheaper than the alternative of re-deriving styles per zoom level.
 */

export interface PaintContext {
  readonly ctx: CanvasRenderingContext2D;
  /** World → screen, in CSS pixels. */
  readonly toScreen: (world: Vec2) => Vec2;
  /** The visible world rectangle, for clipping unbounded geometry. */
  readonly worldBounds: Bounds;
  /** Screen pixels per world unit. */
  readonly zoom: number;
  /**
   * Decoded images, when the caller has any.
   *
   * Optional because most painting has nothing to do with images, and the exporters build a
   * paint context of their own. A context without one simply draws no images, which is what
   * the renderer did before M14.
   */
  readonly images?: { get(href: string): CanvasImageSource | null } | undefined;
}

const DASH_PATTERNS: Readonly<Record<LineStyle, readonly number[]>> = {
  [LineStyle.Solid]: [],
  [LineStyle.Dashed]: [6, 4],
  [LineStyle.Dotted]: [1.5, 3],
};

export function paintValue(
  paint: PaintContext,
  value: Evaluated,
  style: Style,
  /** Which of a polygon's edges the solid covers, when they differ. See {@link paintPolygon}. */
  coveredEdges?: readonly boolean[],
  /** Which of its sides another entity now draws, and this one should therefore not. */
  liftedEdges?: readonly boolean[]
): void {
  const { ctx } = paint;

  ctx.save();
  ctx.globalAlpha = style.opacity;
  ctx.strokeStyle = style.stroke;
  ctx.fillStyle = style.fill;
  ctx.lineWidth = style.strokeWidth;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.setLineDash([...DASH_PATTERNS[style.lineStyle]]);

  switch (value.kind) {
    // A viewpoint is not on the page. Nothing to draw, and nothing to name.
    case 'space':
      break;

    case 'point':
      paintPoint(paint, value.point, style);
      break;

    case 'segment':
      strokePolyline(paint, [value.segment.a, value.segment.b], false);
      /*
       * Arrowheads a style asked for, on the order the segment already has.
       *
       * A segment is stored `a → b` and everything downstream reads that order —
       * `vector-components` returns `b − a` from a segment, and measuring its length gives the
       * same number a vector's does. So the arrow shows an order that is there rather than
       * claiming a new one, which is why this can be presentation and not a construction.
       */
      paintArrows(paint, value.segment.a, value.segment.b, style);
      break;

    case 'vector':
      paintVector(paint, value.from, value.to, style);
      break;

    case 'ray': {
      const visible = clipRayToBounds(value.ray, paint.worldBounds);
      if (visible !== null) {
        strokePolyline(paint, [visible.a, visible.b], false);
        /*
         * A ray carries an arrowhead at its open end, and that is what makes it a ray.
         *
         * Without one a tia and a đoạn thẳng are the same picture: both stop, and nothing says which
         * end goes on for ever. A reader has no way to tell them apart, and the drawing is the only
         * thing they have. Every textbook draws the arrow, and this editor already draws one on each
         * axis for exactly the same reason — the convention and the code were both here, and only the
         * ray was left out.
         *
         * Not a style option. It is notation that says *what the object is*, like the axes' own
         * arrows, and an object that can be drawn as a different object is not a setting worth having.
         *
         * At the clipped end rather than at a fixed length, so it sits where the ray leaves the page —
         * which is the honest place for a mark meaning "and onwards".
         */
        strokeArrowhead(paint, visible.a, visible.b, style);
      }
      break;
    }

    case 'line': {
      const visible = clipLineToBounds(value.line, paint.worldBounds);
      if (visible !== null) strokePolyline(paint, [visible.a, visible.b], false);
      break;
    }

    case 'circle':
      paintEllipticalArc(
        paint,
        value.circle.center,
        value.circle.radius,
        value.circle.radius,
        0,
        0,
        Math.PI * 2,
        false,
        style
      );
      break;

    case 'arc':
      paintEllipticalArc(
        paint,
        value.arc.center,
        value.arc.radius,
        value.arc.radius,
        0,
        value.arc.startAngle,
        value.arc.endAngle,
        value.arc.counterclockwise,
        style,
        conics.arcSweep(value.arc)
      );
      break;

    case 'ellipse':
      paintEllipticalArc(
        paint,
        value.ellipse.center,
        value.ellipse.rx,
        value.ellipse.ry,
        value.ellipse.rotation,
        0,
        Math.PI * 2,
        false,
        style
      );
      break;

    case 'polygon':
      paintPolygon(paint, value.vertices, style, coveredEdges, liftedEdges);
      break;

    case 'polyline': {
      strokePolyline(paint, value.points, value.closed, style);
      // A closed ring has no ends to point from, so it takes none however the style is set.
      const first = value.points[0];
      const second = value.points[1];
      const last = value.points[value.points.length - 1];
      const beforeLast = value.points[value.points.length - 2];
      if (
        !value.closed &&
        first !== undefined &&
        second !== undefined &&
        last !== undefined &&
        beforeLast !== undefined
      ) {
        if (style.arrows === 'start' || style.arrows === 'both') {
          strokeArrowhead(paint, second, first, style);
        }
        if (style.arrows === 'end' || style.arrows === 'both') {
          strokeArrowhead(paint, beforeLast, last, style);
        }
      }
      break;
    }

    /*
     * One stroked path per continuous piece, never joined.
     *
     * Drawing the segments as a single path — even with a `moveTo` between them — would be
     * fine; drawing them as one *point list* would put a vertical line through every asymptote.
     * The separation is decided in the sampler and simply respected here.
     */
    /*
     * Axes, ticks, numbers and the two arrowheads.
     *
     * Drawn before anything is measured against them, and drawn from the geometry the
     * evaluator supplied rather than recomputed here — the same numbers reach the SVG and PDF
     * exporters, which is what keeps a printed worksheet identical to the screen.
     */
    case 'axes': {
      // Three weights rather than one — see `axesStyle`, where the ordering is argued.
      const axis = axisStroke(style);
      const ticks = tickStroke(style);

      for (const segment of value.ticks) {
        strokePolyline(paint, [segment.a, segment.b], false, ticks);
      }
      for (const segment of [value.xAxis, value.yAxis]) {
        strokePolyline(paint, [segment.a, segment.b], false, axis);
      }

      // An arrow on the positive end of each axis: the convention that says which way the
      // numbers grow, and the thing that makes a bare cross read as a coordinate system.
      strokeArrowhead(paint, value.origin, value.xAxis.b, axis);
      strokeArrowhead(paint, value.origin, value.yAxis.b, axis);

      const numbers = numberInk(style);
      for (const mark of value.marks) paintLabel(paint, mark.at, mark.text, numbers);

      const names = nameInk(style);
      for (const name of value.names) paintLabel(paint, name.at, name.text, names);
      break;
    }

    case 'function-graph':
      for (const segment of value.segments) {
        strokePolyline(paint, segment, false, style);
      }

      /*
       * The formula, beside the curve.
       *
       * Written in the label engine's own notation rather than as LaTeX, because the canvas
       * and the two exporters are single-line and share one text layout — `x^{2}` arrives here
       * as `x²`. The panel shows the properly typeset version, which is where a user reads it
       * while working; this is the version that has to survive being printed.
       */
      {
        const label = formulaLabel(value.expression);
        if (label !== null && value.labelAt !== null) {
          paintLabel(paint, value.labelAt, label, style);
        }
      }
      break;

    /*
     * The rules, then the numbers. Both come from the evaluator already placed, so the canvas
     * has no arithmetic of its own to get subtly different from the two exporters'.
     */
    case 'variation-table': {
      for (const rule of value.rules) strokePolyline(paint, [rule.a, rule.b], false, style);
      for (const mark of value.marks) paintCentredLabel(paint, mark.at, mark.text, style);
      break;
    }

    /*
     * Dots, their guides and their numbers — the same three calls the two exporters make.
     *
     * The guides are dashed whatever the style says, because a solid line from a point to the
     * axis is a *construction line* and reads as part of the figure. Dashes are what a textbook
     * uses to mean "read this off", and getting that wrong turns an annotation into a claim.
     */
    case 'graph-marks': {
      const dashed = { ...style, lineStyle: LineStyle.Dashed };
      for (const rule of value.rules) strokePolyline(paint, [rule.a, rule.b], false, dashed);
      for (const point of value.points) paintPoint(paint, point, style);
      for (const mark of value.marks) paintCentredLabel(paint, mark.at, mark.text, style);
      break;
    }

    case 'text':
      paintLabel(paint, value.position, value.content, style);
      break;

    case 'measurement':
      paintLabel(paint, value.position, formatMeasurement(value.value), style);
      break;

    case 'solid':
      if (value.curves !== undefined && value.curves.length > 0) {
        paintSolidCurves(paint, value.curves, value.straight, style);
      } else {
        paintSolid(paint, value.visible, value.hidden, style);
      }
      break;

    case 'angle-mark':
      paintAngleMark(paint, value, style);
      break;

    /*
     * An image whose bytes are not yet decoded draws nothing, and the frame that follows the
     * decode draws it. Not a placeholder box: a box that is replaced a moment later is a flash
     * of wrong content, and on a fast decode the user sees it flicker.
     */
    case 'image': {
      const decoded = paint.images?.get(value.href) ?? null;
      if (decoded === null) break;

      // The world's y axis points up and the image's rows go down, so the anchor is the
      // *top* corner in world terms — which is the smaller screen y.
      const topLeft = paint.toScreen(vec.vec2(value.position.x, value.position.y + value.height));
      ctx.drawImage(
        decoded,
        topLeft.x,
        topLeft.y,
        value.width * paint.zoom,
        value.height * paint.zoom
      );
      break;
    }
  }

  ctx.restore();
}

/**
 * A projected solid: visible edges solid, hidden edges dashed and faded.
 *
 * Two conventions from technical drawing, and both earn their place:
 *
 * - **Dashed** is how a hidden edge is drawn on paper, so the picture reads correctly to
 *   anyone who has seen an engineering drawing — which is the audience.
 * - **Faded** is the addition. On paper the dash pattern alone carries it, because ink is
 *   the only variable; on a screen at low zoom a dashed line and a solid one of the same
 *   weight are hard to tell apart, and the lower alpha keeps the near face reading as near.
 *
 * The hidden edges are drawn *first* so that a visible edge crossing one is unbroken. Drawn
 * the other way round, every crossing would leave a dash lying across a solid line, which
 * is exactly the wrong depth cue.
 */
function paintSolid(
  paint: PaintContext,
  visible: readonly { a: Vec2; b: Vec2 }[],
  hidden: readonly { a: Vec2; b: Vec2 }[],
  style: Style
): void {
  const { ctx } = paint;

  if (hidden.length > 0) {
    ctx.save();
    ctx.globalAlpha = style.opacity * HIDDEN_EDGE_OPACITY;
    ctx.lineWidth = Math.max(0.5, style.strokeWidth * HIDDEN_EDGE_WIDTH);
    ctx.setLineDash(HIDDEN_EDGE_DASH);
    for (const edge of hidden) strokeEdge(paint, edge);
    ctx.restore();
  }

  // The style's own line pattern applies to the visible edges, so a user who wants the
  // whole solid dashed still gets it.
  ctx.setLineDash([...DASH_PATTERNS[style.lineStyle]]);
  for (const edge of visible) strokeEdge(paint, edge);
}

/** One drawn edge of a solid, on the page. */
interface Edge {
  readonly a: Vec2;
  readonly b: Vec2;
}

/**
 * A solid drawn as the curves it actually is, with whatever straight parts it also has.
 *
 * The whole point of the type: `ctx.ellipse` is resolution-independent, so a sphere's outline is
 * round at 1× and round at 40×. A chain of chords is neither, and it is what a reader zooming
 * in to check a figure would see instead.
 *
 * A cylinder needs both halves of this. Its rims are circles and its sides are generator
 * lines — really straight, and a conic there would replace a right answer with a wrong one —
 * so the curves and `straight` are drawn together, and interleaved rather than one after the
 * other so that hidden work stays underneath everything solid.
 *
 * The hidden halves keep exactly the convention the segment path uses — same dash, same weight,
 * same fading — so a sphere sits beside a prism without announcing that it was drawn by a
 * different route.
 */
function paintSolidCurves(
  paint: PaintContext,
  curves: readonly SolidCurve[],
  straight: { visible: readonly Edge[]; hidden: readonly Edge[] } | undefined,
  style: Style
): void {
  const { ctx } = paint;

  for (const hidden of [true, false]) {
    const run = curves.filter((curve) => curve.hidden === hidden);
    const edges = (hidden ? straight?.hidden : straight?.visible) ?? [];
    if (run.length === 0 && edges.length === 0) continue;

    ctx.save();
    if (hidden) {
      ctx.globalAlpha = style.opacity * HIDDEN_EDGE_OPACITY;
      ctx.lineWidth = Math.max(0.5, style.strokeWidth * HIDDEN_EDGE_WIDTH);
      ctx.setLineDash(HIDDEN_EDGE_DASH);
    } else {
      ctx.setLineDash([...DASH_PATTERNS[style.lineStyle]]);
    }

    for (const edge of edges) strokeEdge(paint, edge);

    for (const curve of run) {
      const from = curve.from ?? 0;
      const sweep = curve.sweep ?? Math.PI * 2;
      paintEllipticalArc(
        paint,
        curve.ellipse.center,
        curve.ellipse.rx,
        curve.ellipse.ry,
        curve.ellipse.rotation,
        from,
        from + sweep,
        false,
        style,
        sweep
      );
    }
    ctx.restore();
  }
}

function strokeEdge(paint: PaintContext, edge: Edge): void {
  const { ctx } = paint;
  const from = paint.toScreen(edge.a);
  const to = paint.toScreen(edge.b);
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
}

/** Screen-space dash for hidden edges, matching the convention of technical drawing. */
const HIDDEN_EDGE_DASH: readonly number[] = Object.freeze([5, 4]);
const HIDDEN_EDGE_OPACITY = 0.45;
const HIDDEN_EDGE_WIDTH = 0.85;

/**
 * An angle mark: a square when the angle is right, otherwise one to three arcs.
 *
 * The square is not decoration — it is the notation a reader looks for first, and it means
 * exactly ninety degrees. Because the mark is a construction over the three points, it stops
 * being a square the moment the angle stops being right, which is the behaviour a lesson
 * depends on: drag the figure and the diagram keeps telling the truth.
 *
 * Everything is drawn in screen space at a screen-fixed size, like a point's dot. A mark that
 * grew with the figure would be a shape in the drawing rather than a note about it.
 */
function paintAngleMark(
  paint: PaintContext,
  value: Extract<Evaluated, { kind: 'angle-mark' }>,
  style: Style
): void {
  const geometry = angleMarkGeometry(
    value.vertex,
    value.from,
    value.to,
    value.armLength,
    paint.toScreen,
    {
      nominalRadius: angleMarkNominal(value.showValue),
      size: value.size,
      valueOffset: value.valueOffset,
    }
  );
  // Not enough room to draw something legible. A two-pixel smudge at a vertex is worse than
  // nothing, because the reader sees ink and cannot tell what it means.
  if (geometry === null) return;

  const { ctx } = paint;

  ctx.save();
  /*
   * A mark follows the dash, where most symbols do not.
   *
   * The rest of them — an arrow head, a tick, a point — are drawn solid whatever the style says,
   * because they are notation rather than geometry and a dashed arrow head is just a broken one.
   * An angle mark is different: it sits *somewhere*, in the corner between two arms, and that
   * somewhere can be inside a solid. Drawn solid there it is the one piece of ink in the figure
   * claiming to be in front of a block it is behind.
   *
   * It is `hiddenLines` that decides, from the same test the edges get, and by the time the
   * style arrives here the answer is already in it.
   */
  ctx.setLineDash([...DASH_PATTERNS[style.lineStyle]]);
  ctx.globalAlpha = style.opacity;
  ctx.strokeStyle = style.stroke;
  ctx.lineWidth = Math.max(1, style.strokeWidth);
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';

  if (value.isRight) {
    const [first, far, second] = rightAngleCorners(geometry);
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    ctx.lineTo(far.x, far.y);
    ctx.lineTo(second.x, second.y);
    ctx.stroke();
  } else {
    for (const radius of tickRadii(geometry, value.ticks)) {
      ctx.beginPath();
      ctx.arc(
        geometry.vertex.x,
        geometry.vertex.y,
        radius,
        geometry.startAngle,
        geometry.endAngle,
        geometry.counterclockwise
      );
      ctx.stroke();
    }
  }

  if (value.showValue) {
    const at = valuePosition(geometry, value.ticks);
    const text = `${formatDegrees(value.degrees)}°`;

    ctx.font = `${String(angleValueSize(style))}px ${LABEL_FONT}`;
    ctx.fillStyle = style.stroke;
    // Centred on the bisector, so the number sits in the middle of the angle whichever way
    // the angle happens to open.
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, at.x, at.y);
  }

  ctx.restore();
}

/**
 * The angle, rounded for reading.
 *
 * Whole degrees unless the angle is not whole, because `60°` is what a reader wants and
 * `60.00°` is noise. A right angle shows as `90°` exactly, which the tolerance guarantees.
 */
export function formatDegrees(degrees: number): string {
  const rounded = Math.round(degrees * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/* --------------------------------------------------------------------- primitives */

function paintPoint(paint: PaintContext, world: Vec2, style: Style): void {
  const { ctx } = paint;
  const screen = paint.toScreen(world);
  const r = style.pointSize / 2;

  // Points are drawn at a constant screen size: a vertex must stay clickable and
  // legible at every zoom level, unlike the geometry it belongs to.
  ctx.setLineDash([]);
  ctx.beginPath();

  switch (style.pointStyle) {
    case PointStyle.Dot:
      ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2);
      ctx.fillStyle = style.stroke;
      ctx.fill();
      return;

    case PointStyle.Circle:
      ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2);
      ctx.stroke();
      return;

    case PointStyle.Square:
      ctx.rect(screen.x - r, screen.y - r, r * 2, r * 2);
      ctx.fillStyle = style.stroke;
      ctx.fill();
      return;

    case PointStyle.Cross:
      ctx.moveTo(screen.x - r, screen.y - r);
      ctx.lineTo(screen.x + r, screen.y + r);
      ctx.moveTo(screen.x + r, screen.y - r);
      ctx.lineTo(screen.x - r, screen.y + r);
      ctx.stroke();
      return;

    case PointStyle.Plus:
      ctx.moveTo(screen.x - r, screen.y);
      ctx.lineTo(screen.x + r, screen.y);
      ctx.moveTo(screen.x, screen.y - r);
      ctx.lineTo(screen.x, screen.y + r);
      ctx.stroke();
      return;
  }
}

/**
 * A polygon, stroked edge by edge when the edges disagree about being covered.
 *
 * A section through a solid is the case: `DEF` cut from a tetrahedron has `EF` lying in a face
 * the reader can see and `DF` running straight through the middle of the block. One answer for
 * the whole outline draws the second like the first, and the figure loses the only cue that says
 * which way it faces — a real figure of that problem read flat until this was split.
 *
 * The fill stays one shape, because it is one region. Only the outline is per-edge.
 */
function paintPolygon(
  paint: PaintContext,
  vertices: readonly Vec2[],
  style: Style,
  covered?: readonly boolean[],
  lifted?: readonly boolean[]
): void {
  if (covered === undefined && lifted === undefined) {
    strokePolyline(paint, vertices, true, style);
    return;
  }
  if (vertices.length < 3) return;

  const { ctx } = paint;
  ctx.save();
  ctx.beginPath();
  vertices.forEach((point, index) => {
    const screen = paint.toScreen(point);
    if (index === 0) ctx.moveTo(screen.x, screen.y);
    else ctx.lineTo(screen.x, screen.y);
  });
  ctx.closePath();
  ctx.globalAlpha = style.opacity;
  ctx.fillStyle = style.fill;
  fillIfRequested(ctx, style, screenBoxOf(paint, vertices));
  ctx.restore();

  vertices.forEach((point, index) => {
    const next = vertices[(index + 1) % vertices.length];
    if (next === undefined) return;
    // A side handed over to a segment of its own is not drawn again here; the two would very
    // nearly coincide and a dashed lift would show this one through its gaps.
    if (lifted?.[index] === true) return;
    strokePolyline(paint, [point, next], false, dashedIfCovered(style, covered?.[index]));
  });
}

function strokePolyline(
  paint: PaintContext,
  points: readonly Vec2[],
  closed: boolean,
  style?: Style
): void {
  if (points.length < 2) return;
  const { ctx } = paint;

  ctx.beginPath();
  const first = points[0];
  if (first === undefined) return;
  const start = paint.toScreen(first);
  ctx.moveTo(start.x, start.y);

  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point === undefined) continue;
    const screen = paint.toScreen(point);
    ctx.lineTo(screen.x, screen.y);
  }

  if (closed) ctx.closePath();
  if (closed && style !== undefined) fillIfRequested(ctx, style, screenBoxOf(paint, points));
  ctx.stroke();
}

/**
 * An arc or full conic.
 *
 * Uses `ctx.ellipse`, which takes screen-space radii — so the world radii are scaled by
 * zoom rather than converted point by point. That keeps a circle a true circle at every
 * zoom instead of a polygon approximation, and costs one draw call.
 *
 * The `counterclockwise` flag is inverted relative to the world value: world space is
 * y-up and the canvas is y-down, so a sweep that is counter-clockwise mathematically is
 * clockwise on screen.
 */
function paintEllipticalArc(
  paint: PaintContext,
  center: Vec2,
  rx: number,
  ry: number,
  rotation: number,
  startAngle: number,
  endAngle: number,
  counterclockwise: boolean,
  style: Style,
  sweepOverride?: number
): void {
  const { ctx } = paint;
  const screenCenter = paint.toScreen(center);
  const screenRx = rx * paint.zoom;
  const screenRy = ry * paint.zoom;

  // Sub-pixel conics are invisible and can make the rasteriser misbehave.
  if (screenRx < 0.05 || screenRy < 0.05) return;

  const sweep = sweepOverride ?? Math.abs(endAngle - startAngle);
  const screenStart = -startAngle;
  const screenEnd = counterclockwise ? -startAngle + sweep : -startAngle - sweep;

  ctx.beginPath();
  ctx.ellipse(
    screenCenter.x,
    screenCenter.y,
    screenRx,
    screenRy,
    -rotation,
    screenStart,
    screenEnd,
    !counterclockwise
  );

  if (sweep >= angleMath.TAU - 1e-9) {
    const extent = Math.max(screenRx, screenRy);
    fillIfRequested(ctx, style, {
      minX: screenCenter.x - extent,
      minY: screenCenter.y - extent,
      maxX: screenCenter.x + extent,
      maxY: screenCenter.y + extent,
    });
  }
  ctx.stroke();
}

function paintVector(paint: PaintContext, from: Vec2, to: Vec2, style: Style): void {
  const { ctx } = paint;
  const a = paint.toScreen(from);
  const b = paint.toScreen(to);

  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return;

  // The head is sized in screen pixels and capped at a third of the shaft, so a short
  // vector does not become all arrowhead.
  const headLength = Math.min(12 + style.strokeWidth * 2, length / 3);
  const headWidth = headLength * 0.5;
  const ux = dx / length;
  const uy = dy / length;
  const baseX = b.x - ux * headLength;
  const baseY = b.y - uy * headLength;

  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(baseX, baseY);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(b.x, b.y);
  ctx.lineTo(baseX - uy * headWidth, baseY + ux * headWidth);
  ctx.lineTo(baseX + uy * headWidth, baseY - ux * headWidth);
  ctx.closePath();
  ctx.fillStyle = style.stroke;
  ctx.fill();
}

/**
 * Draws a label, with its markup laid out.
 *
 * Runs through the shared layout rather than painting the raw string, so a subscript lands
 * in the same place on the canvas, in an SVG and in a PDF. Painting the string directly —
 * which this used to do — meant `A_1` read as "A underscore 1" on screen and as `A₁` in an
 * export, and only one of those could be right.
 */
function paintLabel(paint: PaintContext, world: Vec2, text: string, style: Style): void {
  if (text === '') return;
  const { ctx } = paint;
  const screen = paint.toScreen(world);

  const fontPx = labelSize(style);
  const layout = layoutText(text, { fontPx, measure: measurerFor(ctx) });
  if (layout.lines.length === 0) return;

  ctx.setLineDash([]);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = style.stroke;

  layout.lines.forEach((line, index) => {
    const baseline = screen.y - 6 + index * layout.lineHeight;
    for (const run of line) {
      ctx.font = `${String(run.fontPx)}px ${LABEL_FONT}`;
      ctx.fillText(run.text, screen.x + 6 + run.x, baseline + run.y);
    }
  });
}

/**
 * Text centred on its point, rather than set beside it.
 *
 * {@link paintLabel} offsets by a few pixels because it names a thing that is *at* the point —
 * a label on a vertex must not cover the vertex. A table cell is the opposite: the point is
 * the middle of a box, and the number belongs on it.
 */
function paintCentredLabel(paint: PaintContext, world: Vec2, text: string, style: Style): void {
  if (text === '') return;
  const { ctx } = paint;
  const screen = paint.toScreen(world);

  ctx.setLineDash([]);
  ctx.font = `${String(labelSize(style))}px ${LABEL_FONT}`;
  ctx.fillStyle = style.stroke;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, screen.x, screen.y);
}

export const LABEL_FONT = 'system-ui, sans-serif';

/**
 * How far a name sits from the thing it names, in screen pixels.
 *
 * Screen pixels rather than world units, so the gap stays the same at every zoom; a name that
 * drifts away from its point as you zoom out is worse than no name.
 */
export const NAME_GAP = 9;

/**
 * Where an object's name is drawn when nothing better is known.
 *
 * Up and to the right, the textbook corner. This used to be the *only* placement, which is
 * where {@link namePlacement} came from — see `namePlacement.ts` for why a fixed corner is
 * wrong on most real figures.
 */
export const NAME_OFFSET = Object.freeze({ x: 7, y: -7 });

/**
 * The first line's baseline, relative to the named point, in screen pixels with `y` down.
 *
 * Given a **world** direction to write in — `y` up, as everything in `domain` is — this is the
 * one place that turns it into a text position, because the three renderers must agree to the
 * pixel. They previously each added `NAME_OFFSET` themselves, which was fine while the offset
 * was a constant and would not have survived a rule that depends on the text's own width.
 *
 * Two adjustments do the real work, and neither is optional:
 *
 * - **Writing leftwards means shifting by the whole width.** Text grows to the right from
 *   wherever it starts, so a name placed left of its point without this correction runs back
 *   across the point and reads as being on the other side.
 * - **Writing downwards means clearing the ascent.** The baseline is the *bottom* of the
 *   letters, so `y + gap` still leaves them overlapping the point they belong to.
 */
export function namePlacement(
  direction: Vec2,
  layout: { readonly width: number },
  fontPx: number,
  gap: number = NAME_GAP
): { readonly dx: number; readonly dy: number } {
  // Screen `y` grows downward and world `y` upward, so the direction flips on the way in.
  const x = direction.x;
  const y = -direction.y;

  /*
   * `gap` is the only argument an export has to scale; `layout` and `fontPx` arrive already in
   * output units, and scaling the result as a whole would apply the factor to the width
   * correction twice — a mistake that hides at scale 1 and appears as names drifting off their
   * points on a large PDF.
   */
  const dx = x * gap - (x < -0.2 ? layout.width : x <= 0.2 ? layout.width / 2 : 0);

  const ascent = fontPx * 0.72;
  const dy = y * gap + (y > 0.2 ? ascent : y >= -0.2 ? ascent / 2 : 0);

  return { dx, dy };
}

/**
 * Draws an entity's name beside it.
 *
 * Separate from the label *entity* — a name is a property of the object and follows it
 * everywhere, where a text entity is an independent annotation the user positions. Both
 * exist because they answer different needs: "this point is A" and "here is a note".
 */
export function paintName(
  paint: PaintContext,
  at: Vec2,
  name: string,
  style: Style,
  direction?: Vec2
): void {
  if (name === '') return;
  const { ctx } = paint;
  const screen = paint.toScreen(at);

  const fontPx = labelSize(style);
  const layout = layoutText(name, { fontPx, measure: measurerFor(ctx) });
  if (layout.lines.length === 0) return;

  const { dx, dy } = namePlacement(direction ?? DEFAULT_DIRECTION, layout, fontPx);

  ctx.save();
  ctx.setLineDash([]);
  ctx.globalAlpha = style.opacity;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = style.stroke;

  layout.lines.forEach((line, index) => {
    const baseline = screen.y + dy + index * layout.lineHeight;
    for (const run of line) {
      ctx.font = `${String(run.fontPx)}px ${LABEL_FONT}`;
      ctx.fillText(run.text, screen.x + dx + run.x, baseline + run.y);
    }
  });

  ctx.restore();
}

/** The textbook corner, as a world direction. Kept here so `paintName` needs no import cycle. */
const DEFAULT_DIRECTION: Vec2 = Object.freeze(vec.vec2(Math.SQRT1_2, Math.SQRT1_2));

/** Label size follows the point size, so a diagram scaled up keeps its proportions. */
export function labelSize(style: Style): number {
  return Math.max(10, style.pointSize * 2.4);
}

/**
 * The measure written on an angle mark: a step down from an ordinary label.
 *
 * A name and a number are not the same kind of thing. `A` names a point and is part of what the
 * reader is being told; `56.3°` is a measurement *of* something already drawn, and setting it in
 * the same size gives it equal billing with the figure's own letters. Textbooks set the number
 * smaller for exactly that reason.
 *
 * It has to fit somewhere as well. The number goes inside the arc when there is room, and room
 * inside a wedge is scarce — a step down is the difference between fitting and not on angles
 * either side of the threshold.
 */
export function angleValueSize(style: Style): number {
  return Math.max(9, labelSize(style) * 0.85);
}

/**
 * One measurer per context.
 *
 * The cache belongs to the context because that is what the font is set on, and a label is
 * measured on every frame it is painted. Held weakly, so a discarded layer takes its cache
 * with it rather than pinning it for the life of the page.
 */
const measurers = new WeakMap<object, TextMeasurer>();

function measurerFor(ctx: CanvasRenderingContext2D): TextMeasurer {
  const existing = measurers.get(ctx);
  if (existing !== undefined) return existing;
  const created = createMeasurer(ctx, LABEL_FONT);
  measurers.set(ctx, created);
  return created;
}

/**
 * Fills the current path according to the style.
 *
 * Hatching is drawn by clipping to the path and stroking parallel lines across its box,
 * rather than by building a `CanvasPattern` from an offscreen canvas. Two reasons:
 *
 * - A pattern is anchored to the canvas origin, so a hatched shape's stripes would crawl
 *   across it as the user pans — the fill would appear to slide within its own outline.
 * - It needs a second canvas per colour and a cache keyed on it, for a fill that a
 *   textbook diagram uses on perhaps two shapes.
 *
 * The spacing is in screen pixels, so hatching keeps its density at every zoom level,
 * exactly like stroke width and dash patterns.
 */
function fillIfRequested(ctx: CanvasRenderingContext2D, style: Style, bounds?: ScreenBox): void {
  if (style.fillStyle === 'none' || style.fill === 'transparent') return;

  if (style.fillStyle !== 'hatch') {
    ctx.fill();
    return;
  }
  if (bounds === undefined) {
    // No box to hatch across; a solid fill is closer to the intent than nothing at all.
    ctx.fill();
    return;
  }

  ctx.save();
  ctx.clip();

  ctx.strokeStyle = style.fill;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);

  const spacing = HATCH_SPACING;
  const { minX, minY, maxX, maxY } = bounds;
  const width = maxX - minX;
  const height = maxY - minY;

  ctx.beginPath();
  // 45-degree lines: sweeping the intercept from -height to +width covers the whole box.
  for (let offset = -height; offset <= width; offset += spacing) {
    ctx.moveTo(minX + offset, minY);
    ctx.lineTo(minX + offset + height, maxY);
  }
  ctx.stroke();
  ctx.restore();
}

const HATCH_SPACING = 7;

interface ScreenBox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** Screen-space bounding box of a set of world points. */
function screenBoxOf(paint: PaintContext, points: readonly Vec2[]): ScreenBox | undefined {
  if (points.length === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    const screen = paint.toScreen(point);
    if (screen.x < minX) minX = screen.x;
    if (screen.y < minY) minY = screen.y;
    if (screen.x > maxX) maxX = screen.x;
    if (screen.y > maxY) maxY = screen.y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * A filled arrowhead at `to`, pointing away from `from`.
 *
 * Sized in **screen** pixels like every other piece of notation: an arrow is a mark *about* the
 * drawing rather than a part of it, so it stays legible when the axes are zoomed right out and
 * does not swell into a blot when they are zoomed in.
 */
/** The heads a style asks for on a two-ended thing. */
function paintArrows(paint: PaintContext, a: Vec2, b: Vec2, style: Style): void {
  if (style.arrows === 'start' || style.arrows === 'both') strokeArrowhead(paint, b, a, style);
  if (style.arrows === 'end' || style.arrows === 'both') strokeArrowhead(paint, a, b, style);
}

function strokeArrowhead(paint: PaintContext, from: Vec2, to: Vec2, style: Style): void {
  const tip = paint.toScreen(to);
  const tail = paint.toScreen(from);

  const away = vec.subtract(tip, tail);
  if (vec.isZero(away)) return;

  const direction = vec.normalize(away);
  const across = vec.vec2(-direction.y, direction.x);
  const base = vec.subtract(tip, vec.scale(direction, ARROWHEAD_LENGTH));

  const { ctx } = paint;
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(base.x + across.x * ARROWHEAD_HALF_WIDTH, base.y + across.y * ARROWHEAD_HALF_WIDTH);
  ctx.lineTo(base.x - across.x * ARROWHEAD_HALF_WIDTH, base.y - across.y * ARROWHEAD_HALF_WIDTH);
  ctx.closePath();
  ctx.fillStyle = style.stroke;
  ctx.fill();
}

const ARROWHEAD_LENGTH = 9;
const ARROWHEAD_HALF_WIDTH = 3.5;
